import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, where, doc, updateDoc, addDoc,
         getDoc, serverTimestamp, arrayUnion, increment } from 'firebase/firestore';
import { UserAuth } from '../../AuthContext';
import { Box, Flex, Text, Heading, Spinner, Center } from '@chakra-ui/react';
import TokenService from '../../services/token/TokenService';

const tokenService = new TokenService();

// ── HAVERSINE ──────────────────────────────────────────────────────────────
const haversine = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = (lat2-lat1)*Math.PI/180;
  const dLng = (lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const LEVEL_CONFIG: Record<number, { label: string; color: string; icon: string; bg: string }> = {
  0: { label: 'Community Submitted',  color: '#60A5FA', icon: '🔵', bg: '#0D1A2E' },
  1: { label: 'Community Confirmed',  color: '#FBBF24', icon: '🟡', bg: '#1A1200' },
  2: { label: 'TD Verified',          color: '#22C55E', icon: '🟢', bg: '#0A1A10' },
  3: { label: 'PokerCity Verified',   color: '#19E6CF', icon: '✅', bg: '#0D1F1E' },
};

const RADIUS_OPTIONS = [25, 50, 100, 250, 9999];
const RADIUS_LABELS: Record<number, string> = { 25:'25 km', 50:'50 km', 100:'100 km', 250:'250 km', 9999:'Province-wide' };
const DAYS = ['Any','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };
const C = { card:'#131720',border:'#1E2330',gold:'#D4A017',teal:'#19E6CF',white:'#F0F4FF',ink:'#C8D0E0',dim:'#6B7590',green:'#22C55E',red:'#EF4444' };

const FindAGamePage: FC = () => {
  const { user } = UserAuth();
  const navigate = useNavigate();

  const [searchCity, setSearchCity] = useState('');
  const [searchLat, setSearchLat] = useState<number|null>(null);
  const [searchLng, setSearchLng] = useState<number|null>(null);
  const [searchRadius, setSearchRadius] = useState(50);
  const [dayFilter, setDayFilter] = useState('Any');
  const [geocoding, setGeocoding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState<any[]>([]);
  const [searched, setSearched] = useState(false);
  const [corroborating, setCorroborating] = useState<string|null>(null);
  const [claiming, setClaiming] = useState<string|null>(null);
  const [userCorroborations, setUserCorroborations] = useState<Set<string>>(new Set());
  const [expandedGame, setExpandedGame] = useState<string|null>(null);

  const geocodeAndSearch = async () => {
    if (!searchCity.trim()) return;
    setGeocoding(true);
    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchCity)}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
      );
      const data = await resp.json();
      if (data.results?.[0]?.geometry?.location) {
        const { lat, lng } = data.results[0].geometry.location;
        setSearchLat(lat);
        setSearchLng(lng);
        setSearchCity(data.results[0].formatted_address);
        await runSearch(lat, lng);
      }
    } catch {}
    setGeocoding(false);
  };

  const runSearch = async (lat: number, lng: number) => {
    setLoading(true);
    setSearched(true);
    try {
      const snap = await getDocs(query(collection(db, 'games'), where('status', '==', 'active')));
      const results: any[] = [];
      const myCorroborations = new Set<string>();

      for (const d of snap.docs) {
        const g = d.data();
        if (!g.lat || !g.lng) {
          // Include games in same city even without coords
          if (g.city?.toLowerCase().includes(searchCity.split(',')[0].toLowerCase())) {
            results.push({ id: d.id, ...g, distance_km: null });
          }
          continue;
        }
        const dist = haversine(lat, lng, g.lat, g.lng);
        if (dist > searchRadius) continue;
        results.push({ id: d.id, ...g, distance_km: Math.round(dist) });
        if (g.corroborations?.includes(user?.uid)) myCorroborations.add(d.id);
      }

      // Apply day filter
      const filtered = dayFilter === 'Any'
        ? results
        : results.filter(g => g.schedule?.day === dayFilter || g.schedule?.day === 'Varies');

      // Sort: verified first, then by distance
      filtered.sort((a, b) => {
        if ((b.verification_level ?? 0) !== (a.verification_level ?? 0))
          return (b.verification_level ?? 0) - (a.verification_level ?? 0);
        return (a.distance_km ?? 999) - (b.distance_km ?? 999);
      });

      setGames(filtered);
      setUserCorroborations(myCorroborations);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const corroborate = async (gameId: string, game: any) => {
    if (!user?.uid || corroborating || userCorroborations.has(gameId)) return;
    if (game.submitter_uid === user.uid) return; // Can't corroborate own submission
    setCorroborating(gameId);
    try {
      const gameRef = doc(db, 'games', gameId);
      await updateDoc(gameRef, {
        corroborations: arrayUnion(user.uid),
        corroboration_count: increment(1),
      });

      // Check if corroboration count now qualifies for level 1
      const newCount = (game.corroboration_count ?? 0) + 1;
      if (newCount >= 2 && (game.verification_level ?? 0) < 1) {
        await updateDoc(gameRef, { verification_level: 1, verification_score: increment(1) });
        // Award 5 tokens to each corroborator + original submitter
        if (game.submitter_uid) {
          await tokenService.awardTokens(game.submitter_uid, 5, 'bonus' as any, `Game corroborated: ${game.name}`);
        }
      }
      // Award 5 tokens to this corroborator
      await tokenService.awardTokens(user.uid, 5, 'bonus' as any, `Corroborated game: ${game.name}`);

      setUserCorroborations(s => new Set([...s, gameId]));
      setGames(gs => gs.map(g => g.id === gameId
        ? { ...g, corroboration_count: (g.corroboration_count ?? 0) + 1,
            verification_level: newCount >= 2 && (g.verification_level ?? 0) < 1 ? 1 : (g.verification_level ?? 0) }
        : g));
    } catch (e) { console.error(e); }
    setCorroborating(null);
  };

  const claimGame = async (gameId: string, game: any) => {
    if (!user?.uid || claiming) return;
    setClaiming(gameId);
    try {
      const gameRef = doc(db, 'games', gameId);
      await updateDoc(gameRef, {
        claimed_by_uid: user.uid,
        td_uid: user.uid,
        verification_level: 2,
        verification_score: increment(2),
        claimed_at: serverTimestamp(),
      });

      // Award 25 tokens to first submitter
      if (game.submitter_uid && game.submitter_uid !== user.uid) {
        await tokenService.awardTokens(
          game.submitter_uid, 25, 'bonus' as any,
          `Your submission "${game.name}" was claimed by the TD — thank you for contributing to the directory!`
        );
        // Notify the submitter
        await addDoc(collection(db, 'users', game.submitter_uid, 'notifications'), {
          type: 'game_claimed',
          recipientUid: game.submitter_uid,
          eventId: gameId,
          eventName: game.name,
          actorName: user.displayName ?? 'A Tournament Director',
          message: `🏆 Your submission "${game.name}" was claimed and verified — you earned 25 PC Tokens!`,
          urgent: false,
          read: false,
          createdAt: serverTimestamp(),
        });
      }

      setGames(gs => gs.map(g => g.id === gameId
        ? { ...g, verification_level: 2, claimed_by_uid: user.uid, td_uid: user.uid }
        : g));
    } catch (e) { console.error(e); }
    setClaiming(null);
  };

  return (
    <Box maxW="720px" mx="auto" px={4} py={8} fontFamily={F.ui}>

      {/* Header */}
      <Flex align="flex-start" justify="space-between" mb={6}>
        <Box>
          <Heading style={{ fontFamily: F.ui, color: C.white, fontSize: 28, marginBottom: 6 }}>
            Find a Game
          </Heading>
          <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim, lineHeight: 1.6 }}>
            Community-sourced directory of regular poker games.
            Know of a game? <span style={{ color: C.teal, cursor:'pointer', fontWeight:700 }}
              onClick={() => navigate('/submit-game')}>Submit it →</span>
          </Text>
        </Box>
        <Box as="button" px={4} py={2} bg="#0D1F1E" border={`1px solid ${C.teal}`}
          borderRadius={8} fontSize="13px" fontWeight="700" color={C.teal}
          fontFamily={F.ui} cursor="pointer"
          onClick={() => navigate('/submit-game')}>
          + Submit a Game
        </Box>
      </Flex>

      {/* Verification legend */}
      <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={8} p={3} mb={4}>
        <Text style={{ fontFamily: F.mono, fontSize: 10, color: C.dim, letterSpacing:'0.15em', marginBottom:8 }}>
          VERIFICATION LEVELS
        </Text>
        <Flex gap={4} flexWrap="wrap">
          {Object.entries(LEVEL_CONFIG).map(([level, cfg]) => (
            <Flex key={level} align="center" gap={2}>
              <Text style={{ fontSize: 14 }}>{cfg.icon}</Text>
              <Text style={{ fontFamily: F.ui, fontSize: 12, color: cfg.color }}>{cfg.label}</Text>
            </Flex>
          ))}
        </Flex>
      </Box>

      {/* Search */}
      <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} p={4} mb={4}>
        <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.teal, letterSpacing:'0.2em', marginBottom:12 }}>
          SEARCH
        </Text>
        <Flex gap={2} mb={3} flexWrap="wrap">
          <input type="text" placeholder="City or town..."
            value={searchCity} onChange={e => setSearchCity(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && geocodeAndSearch()}
            style={{ flex:1, minWidth:180, background:'#0D1118', border:`1px solid ${C.border}`,
              borderRadius:6, padding:'10px 14px', color:C.white, fontSize:14, fontFamily:F.ui }} />
          <Box as="button" px={4} py={2} bg="#052E16" border="1px solid #166534"
            borderRadius={6} fontSize="13px" fontWeight="700" color="#86EFAC"
            fontFamily={F.ui} cursor={geocoding?'wait':'pointer'} onClick={geocodeAndSearch}>
            {geocoding ? 'Searching...' : 'Search'}
          </Box>
        </Flex>
        <Flex gap={2} flexWrap="wrap" mb={3}>
          <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim, letterSpacing:'0.1em', alignSelf:'center' }}>RADIUS:</Text>
          {RADIUS_OPTIONS.map(r => (
            <Box key={r} as="button" px={3} py={1}
              bg={searchRadius===r?'#0D1F1E':C.card}
              border={`1px solid ${searchRadius===r?C.teal:C.border}`}
              borderRadius={6} fontSize="12px" fontWeight="700"
              color={searchRadius===r?C.teal:C.dim} fontFamily={F.mono} cursor="pointer"
              onClick={() => { setSearchRadius(r); if(searchLat&&searchLng) runSearch(searchLat,searchLng); }}>
              {RADIUS_LABELS[r]}
            </Box>
          ))}
        </Flex>
        <Flex gap={2} flexWrap="wrap">
          <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim, letterSpacing:'0.1em', alignSelf:'center' }}>DAY:</Text>
          {DAYS.map(d => (
            <Box key={d} as="button" px={3} py={1}
              bg={dayFilter===d?'#1A0F00':C.card}
              border={`1px solid ${dayFilter===d?C.gold:C.border}`}
              borderRadius={6} fontSize="12px" fontWeight="700"
              color={dayFilter===d?C.gold:C.dim} fontFamily={F.mono} cursor="pointer"
              onClick={() => { setDayFilter(d); if(searchLat&&searchLng) runSearch(searchLat,searchLng); }}>
              {d}
            </Box>
          ))}
        </Flex>
      </Box>

      {/* Results */}
      {loading && <Center py={12}><Spinner size="xl" color="teal.400" /></Center>}

      {!loading && searched && games.length === 0 && (
        <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} p={8} textAlign="center">
          <Text style={{ fontSize:36, marginBottom:12 }}>🃏</Text>
          <Text style={{ fontFamily:F.ui, fontSize:16, color:C.white, marginBottom:8 }}>No games found in this area</Text>
          <Text style={{ fontFamily:F.ui, fontSize:13, color:C.dim, marginBottom:20 }}>
            Know of a game here? Be the first to submit it.
          </Text>
          <Box as="button" px={5} py={3} bg="#052E16" border="1px solid #166534"
            borderRadius={8} fontSize="13px" fontWeight="700" color="#86EFAC"
            fontFamily={F.ui} cursor="pointer" onClick={() => navigate('/submit-game')}>
            Submit a Game →
          </Box>
        </Box>
      )}

      {!loading && !searched && (
        <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} p={8} textAlign="center">
          <Text style={{ fontSize:36, marginBottom:12 }}>🗺</Text>
          <Text style={{ fontFamily:F.ui, fontSize:16, color:C.white, marginBottom:8 }}>Enter a location to find games</Text>
          <Text style={{ fontFamily:F.ui, fontSize:13, color:C.dim }}>Search by city or town to see regular games in your area.</Text>
        </Box>
      )}

      {!loading && games.map(game => {
        const level = LEVEL_CONFIG[game.verification_level ?? 0];
        const alreadyCorroborated = userCorroborations.has(game.id);
        const isOwn = game.submitter_uid === user?.uid;
        const isClaimed = !!game.claimed_by_uid;
        const isMyGame = game.td_uid === user?.uid;
        const isExpanded = expandedGame === game.id;

        return (
          <Box key={game.id} bg={C.card} border={`1px solid ${C.border}`}
            borderRadius={10} overflow="hidden" mb={3}
            style={{ borderLeftColor: level.color, borderLeftWidth: 3 }}>

            {/* Card header */}
            <Flex bg={level.bg} px={4} py={2} align="center" justify="space-between">
              <Flex align="center" gap={2}>
                <Text style={{ fontSize:14 }}>{level.icon}</Text>
                <Text style={{ fontFamily:F.mono, fontSize:10, color:level.color, letterSpacing:'0.15em' }}>
                  {level.label.toUpperCase()}
                </Text>
              </Flex>
              {game.distance_km !== null && game.distance_km !== undefined && (
                <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim }}>
                  {game.distance_km} km away
                </Text>
              )}
            </Flex>

            {/* Card body */}
            <Box p={4}>
              <Flex align="flex-start" justify="space-between" mb={2}>
                <Box flex={1}>
                  <Text style={{ fontFamily:F.ui, fontSize:18, fontWeight:700, color:C.white, marginBottom:2 }}>
                    {game.name}
                  </Text>
                  <Text style={{ fontFamily:F.ui, fontSize:13, color:C.dim }}>
                    {game.city}{game.province ? `, ${game.province}` : ''}{game.area ? ` — ${game.area}` : ''}
                  </Text>
                </Box>
              </Flex>

              {/* Key details */}
              <Flex gap={3} flexWrap="wrap" my={3}>
                {game.schedule?.day && (
                  <Box px={2} py={1} bg="#131720" border={`1px solid ${C.border}`} borderRadius={4}>
                    <Text style={{ fontFamily:F.mono, fontSize:11, color:C.ink }}>
                      📅 {game.schedule.day}{game.schedule.time ? ` · ${game.schedule.time}` : ''}
                    </Text>
                  </Box>
                )}
                {game.game_type && (
                  <Box px={2} py={1} bg="#131720" border={`1px solid ${C.border}`} borderRadius={4}>
                    <Text style={{ fontFamily:F.mono, fontSize:11, color:C.ink }}>🃏 {game.game_type}</Text>
                  </Box>
                )}
                {game.buy_in_range && (
                  <Box px={2} py={1} bg="#131720" border={`1px solid ${C.border}`} borderRadius={4}>
                    <Text style={{ fontFamily:F.mono, fontSize:11, color:C.ink }}>💰 {game.buy_in_range}</Text>
                  </Box>
                )}
                {game.blinds && (
                  <Box px={2} py={1} bg="#131720" border={`1px solid ${C.border}`} borderRadius={4}>
                    <Text style={{ fontFamily:F.mono, fontSize:11, color:C.ink }}>⚡ {game.blinds}</Text>
                  </Box>
                )}
              </Flex>

              {/* Expanded details */}
              {isExpanded && (
                <Box mb={3} p={3} bg="#0D1118" borderRadius={6} border={`1px solid ${C.border}`}>
                  {game.max_players && (
                    <Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink, marginBottom:4 }}>
                      👥 Max players: {game.max_players}
                    </Text>
                  )}
                  {game.contact_method && game.contact_value && (
                    <Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink, marginBottom:4 }}>
                      📞 Contact: {game.contact_method} — {game.contact_value}
                    </Text>
                  )}
                  {game.notes && (
                    <Text style={{ fontFamily:F.ui, fontSize:13, color:C.dim, fontStyle:'italic' }}>
                      {game.notes}
                    </Text>
                  )}
                  <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, marginTop:8 }}>
                    Submitted by {game.submitter_name ?? 'anonymous'} · {game.corroboration_count ?? 0} corroboration{game.corroboration_count === 1 ? '' : 's'}
                  </Text>
                </Box>
              )}

              {/* Actions */}
              <Flex gap={2} flexWrap="wrap" align="center">
                <Box as="button" px={3} py={2} bg={C.card} border={`1px solid ${C.border}`}
                  borderRadius={6} fontSize="12px" fontWeight="700" color={C.dim}
                  fontFamily={F.ui} cursor="pointer"
                  onClick={() => setExpandedGame(isExpanded ? null : game.id)}>
                  {isExpanded ? 'Less ↑' : 'Details ↓'}
                </Box>

                {/* Corroborate — logged in, not own submission, not already corroborated */}
                {user && !isOwn && !alreadyCorroborated && (
                  <Box as="button" px={3} py={2}
                    bg="#0D1520" border={`1px solid ${C.gold}`}
                    borderRadius={6} fontSize="12px" fontWeight="700" color={C.gold}
                    fontFamily={F.ui} cursor={corroborating===game.id?'wait':'pointer'}
                    onClick={() => corroborate(game.id, game)}>
                    {corroborating===game.id ? 'Confirming...' : '✓ I know this game'}
                  </Box>
                )}
                {alreadyCorroborated && (
                  <Text style={{ fontFamily:F.mono, fontSize:11, color:C.green }}>✓ Confirmed by you</Text>
                )}

                {/* Claim — logged in, unclaimed, not own submission */}
                {user && !isClaimed && !isOwn && (
                  <Box as="button" px={3} py={2}
                    bg="#052E16" border="1px solid #166534"
                    borderRadius={6} fontSize="12px" fontWeight="700" color="#86EFAC"
                    fontFamily={F.ui} cursor={claiming===game.id?'wait':'pointer'}
                    onClick={() => claimGame(game.id, game)}>
                    {claiming===game.id ? 'Claiming...' : 'This is my game — Claim it'}
                  </Box>
                )}

                {/* Already claimed by user */}
                {isMyGame && (
                  <Box px={3} py={2} bg="#0D1F1E" border={`1px solid ${C.teal}`}
                    borderRadius={6}>
                    <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal }}>✓ YOUR GAME</Text>
                  </Box>
                )}
              </Flex>
            </Box>
          </Box>
        );
      })}

    </Box>
  );
};

export default FindAGamePage;
